import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, Download, ShieldCheck, Copy, Filter, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  paid: { label: "Pagato", cls: "text-forensic-green bg-forensic-green/10 border-forensic-green/30" },
  pending: { label: "In attesa", cls: "text-forensic-amber bg-forensic-amber/10 border-forensic-amber/30" },
  failed: { label: "Fallito", cls: "text-forensic-red bg-forensic-red/10 border-forensic-red/30" },
};

function StatusPill({ payment_status }) {
  const s = STATUS_LABELS[payment_status] || { label: payment_status || "—", cls: "text-slate-600 bg-slate-100 border-slate-300" };
  return <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wider border rounded-sm ${s.cls}`}>{s.label}</span>;
}

function formatEur(cents) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);
}

export default function AdminOrders() {
  const { user, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const allowed = user ? isAdmin : false;

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/orders", { withCredentials: true });
      setOrders(r.data.orders || []);
      setTotals(r.data.totals || null);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Solo per amministratori");
      else toast.error("Errore caricamento");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/orders.csv`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ordini-perizia-ready-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore export CSV"); }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => o.payment_status === filter);
  }, [orders, filter]);

  const copyRow = (o) => {
    const rows = [
      `Data pagamento: ${o.updated_at || o.created_at || "—"}`,
      `N° fattura: ${o.invoice_num || "—"}`,
      `Cliente: ${[o.customer_first_name, o.customer_last_name].filter(Boolean).join(" ")}`,
      `Email: ${o.customer_email || "—"}`,
      o.customer_phone && `Telefono: ${o.customer_phone}`,
      o.customer_fiscal_code && `Codice fiscale: ${o.customer_fiscal_code}`,
      o.customer_vat_number && `Partita IVA: ${o.customer_vat_number}`,
      o.customer_pec && `PEC: ${o.customer_pec}`,
      o.customer_sdi_code && `Codice SDI: ${o.customer_sdi_code}`,
      `Indirizzo: ${[o.customer_address_street, o.customer_address_zip, o.customer_address_city, o.customer_address_province].filter(Boolean).join(", ")}`,
      `Totale: ${formatEur(o.amount_cents)}`,
      `Regime: Forfettario (senza IVA, art. 1 c.54-89 L. 190/2014)`,
      `Session ID: ${o.session_id}`,
      `Fascicolo: ${window.location.origin}/case/${o.case_id}`,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(rows).then(() => toast.success("Dati copiati"));
  };

  if (allowed === false) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Accesso riservato</h1>
            <p className="text-sm text-slate-600 mt-2">
              Solo per amministratori. Se sei il perito titolare, accedi con l'account Google configurato in <code className="font-mono text-xs">ADMIN_EMAILS</code>.
            </p>
            <Link to="/" className="inline-block mt-4 text-xs border hairline hover:border-navy px-4 py-2 rounded-sm">Torna alla home</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto px-6 py-10 w-full">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <div className="overline mb-1">Area amministratore</div>
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">Ordini fascicoli · € 29,90</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Pagamenti Stripe con dati fiscali del cliente per fatturazione manuale in <b>regime forfettario</b>.
              Copia i dati con un click o esporta l'intero elenco pagato in CSV.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} disabled={refreshing}
              className="inline-flex items-center gap-2 border hairline hover:border-navy px-4 py-2 rounded-sm text-sm disabled:opacity-60"
              data-testid="admin-orders-refresh">
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aggiorna
            </button>
            <button onClick={downloadCsv}
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-4 py-2 rounded-sm text-sm"
              data-testid="admin-orders-csv">
              <Download size={14} /> Esporta CSV
            </button>
          </div>
        </div>

        {totals && (
          <section className="grid md:grid-cols-3 gap-4 mb-6" data-testid="admin-totals">
            <div className="border hairline bg-white p-5">
              <div className="overline">Totale incassato</div>
              <div className="mt-2 font-serif text-3xl font-semibold">{formatEur(totals.total_paid_cents)}</div>
            </div>
            <div className="border hairline bg-white p-5">
              <div className="overline">Ordini pagati</div>
              <div className="mt-2 font-serif text-3xl font-semibold">{totals.count_paid}</div>
            </div>
            <div className="border hairline bg-white p-5">
              <div className="overline">In attesa</div>
              <div className="mt-2 font-serif text-3xl font-semibold">{totals.count_pending}</div>
            </div>
          </section>
        )}

        <div className="flex items-center gap-2 mb-4">
          <Filter size={14} className="text-slate-500" />
          {[["all", "Tutti"], ["paid", "Pagati"], ["pending", "In attesa"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 rounded-sm text-xs border ${filter === k ? "bg-navy text-white border-navy" : "hairline bg-white hover:border-navy"}`}
              data-testid={`filter-${k}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="border hairline bg-white overflow-hidden" data-testid="admin-orders-container">
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin text-navy mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">Nessun ordine trovato.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy text-white text-xs uppercase tracking-wider">
                  <th className="px-3 py-3 text-left">Data</th>
                  <th className="px-3 py-3 text-left">N° fattura</th>
                  <th className="px-3 py-3 text-left">Cliente</th>
                  <th className="px-3 py-3 text-left">CF / P.IVA</th>
                  <th className="px-3 py-3 text-right">Importo</th>
                  <th className="px-3 py-3 text-center">Stato</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <Fragment key={o.session_id}>
                    <tr className="border-t hairline hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpanded(expanded === o.session_id ? null : o.session_id)}
                      data-testid={`admin-order-${o.session_id}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        {o.created_at ? new Date(o.created_at).toLocaleString("it-IT") : "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{o.invoice_num || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm">{[o.customer_first_name, o.customer_last_name].filter(Boolean).join(" ") || <span className="text-slate-400">—</span>}</div>
                        <div className="text-xs text-slate-500 font-mono">{o.customer_email}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        <div>{o.customer_fiscal_code || <span className="text-slate-400">—</span>}</div>
                        {o.customer_vat_number && <div className="text-slate-500">P.IVA {o.customer_vat_number}</div>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{formatEur(o.amount_cents)}</td>
                      <td className="px-3 py-2 text-center"><StatusPill payment_status={o.payment_status} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          {o.case_id && (
                            <Link to={`/case/${o.case_id}`} target="_blank" onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs border hairline hover:border-navy px-2 py-1 rounded-sm">
                              <ExternalLink size={11} />
                            </Link>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); copyRow(o); }}
                            className="inline-flex items-center gap-1 text-xs border hairline hover:border-navy px-2 py-1 rounded-sm"
                            data-testid={`copy-${o.session_id}`}>
                            <Copy size={11} /> Copia
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded === o.session_id && (
                      <tr className="bg-paper border-t hairline">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid md:grid-cols-2 gap-6 text-sm">
                            <div>
                              <div className="overline mb-2">Anagrafica</div>
                              <dl className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1">
                                <dt className="text-slate-500">Nome</dt><dd>{o.customer_first_name || "—"}</dd>
                                <dt className="text-slate-500">Cognome</dt><dd>{o.customer_last_name || "—"}</dd>
                                <dt className="text-slate-500">Email</dt><dd className="font-mono text-xs">{o.customer_email}</dd>
                                <dt className="text-slate-500">Telefono</dt><dd className="font-mono text-xs">{o.customer_phone || "—"}</dd>
                                <dt className="text-slate-500">PEC</dt><dd className="font-mono text-xs">{o.customer_pec || "—"}</dd>
                                <dt className="text-slate-500">Codice fiscale</dt><dd className="font-mono">{o.customer_fiscal_code || "—"}</dd>
                                <dt className="text-slate-500">Partita IVA</dt><dd className="font-mono">{o.customer_vat_number || "—"}</dd>
                                <dt className="text-slate-500">Codice SDI</dt><dd className="font-mono">{o.customer_sdi_code || "0000000"}</dd>
                              </dl>
                            </div>
                            <div>
                              <div className="overline mb-2">Sede</div>
                              <dl className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1">
                                <dt className="text-slate-500">Indirizzo</dt><dd>{o.customer_address_street || "—"}</dd>
                                <dt className="text-slate-500">CAP</dt><dd className="font-mono">{o.customer_address_zip || "—"}</dd>
                                <dt className="text-slate-500">Città</dt><dd>{o.customer_address_city || "—"}</dd>
                                <dt className="text-slate-500">Provincia</dt><dd className="font-mono">{o.customer_address_province || "—"}</dd>
                              </dl>
                              <div className="overline mt-4 mb-2">Pagamento</div>
                              <dl className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1">
                                <dt className="text-slate-500">Session</dt><dd className="font-mono text-xs break-all">{o.session_id}</dd>
                                <dt className="text-slate-500">Fascicolo</dt><dd className="font-mono text-xs break-all">{o.case_id}</dd>
                                <dt className="text-slate-500">Regime</dt><dd className="text-xs">Forfettario — senza IVA (L. 190/2014)</dd>
                              </dl>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
